import { useCallback } from "react"

import { EditableTitle } from "@coval/ui"

import { useDetachedSaveFailureToast } from "@/hooks/useDetachedSaveFailureToast"
import { validateResourceName } from "@/utils/validation/resourceName"

interface RowNameEditorProps {
  /** The saved name the field starts from. */
  value: string
  /** Accessible name of the field, e.g. "Agent name". */
  label: string
  /** The resource as it reads in a sentence, e.g. "agent", for validation messages. */
  resource: string
  /** Shown inside the field when it is emptied. */
  placeholder?: string
  /** Longest name accepted, in code points. */
  maxLength?: number
  /** Whether another loaded row already uses the name; such a name is blocked with an inline error. */
  isNameTaken?: (name: string) => boolean
  /** Persists the trimmed name. Rejecting keeps the field open with the error, or raises a toast once the field has closed. */
  onSave: (name: string) => Promise<void>
  /** Called once the field closes, after a save, Escape, or an unchanged name. */
  onClose: () => void
}

/**
 * The name cell of a list row while it is being renamed from the row's menu. It mounts already open, saves on Enter or blur, and closes on Escape. The wrapper keeps clicks in the field from opening the row.
 */
export function RowNameEditor({
  value,
  label,
  resource,
  placeholder,
  maxLength,
  isNameTaken,
  onSave,
  onClose,
}: RowNameEditorProps) {
  const validate = useCallback(
    (name: string) => {
      const result = validateResourceName(name, {
        resource,
        maxLength,
        validate: (trimmed) =>
          isNameTaken?.(trimmed)
            ? `Another ${resource} already uses this name.`
            : undefined,
      })
      return result.status === "invalid" ? result.message : undefined
    },
    [isNameTaken, maxLength, resource],
  )
  const notifyDetachedFailure = useDetachedSaveFailureToast({
    title: `Couldn't rename the ${resource}`,
    fallback: "The name was not saved. Try again.",
  })

  return (
    <div data-prevent-row-click="true" className="min-w-0">
      <EditableTitle
        size="sm"
        trigger="external"
        editing
        onEditingChange={(editing) => {
          if (!editing) {
            onClose()
          }
        }}
        label={label}
        value={value}
        placeholder={placeholder}
        validate={validate}
        onSave={(name) => notifyDetachedFailure(onSave(name))}
      />
    </div>
  )
}
